import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../lib/apiContext';
import { queryKeys } from '../../lib/queryKeys';
import type {
  SiteStatus,
  LocalStartResponse,
  AddStagingRequest,
  AddStagingResponse,
  LogChannelsResponse,
  LogTailResponse,
  LogToggleResponse,
  EditorUsageResponse,
  EditorKeyStatus,
  ChatsResponse,
  SiteUsageResponse,
} from '../../lib/types';

/** GET /sites/{id}/status */
export function useSiteStatus(siteId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteStatus(siteId),
    queryFn: () => client.get<SiteStatus>(`/sites/${siteId}/status`),
  });
}

/** POST /sites/{id}/local/start — starts a stopped LOCAL site's Docker compose project (route doesn't exist on the prod manager); refetches status after. */
export function useStartLocalSite(siteId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.post<LocalStartResponse>(`/sites/${siteId}/local/start`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.siteStatus(siteId) }),
  });
}

/** POST /sites/{id}/staging — enters a SiteGround staging copy (gateway wires the store row + wp-cli.conf line). */
export function useAddStaging(siteId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddStagingRequest) =>
      client.post<AddStagingResponse>(`/sites/${siteId}/staging`, body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.siteStatus(siteId) }),
  });
}

/** GET /sites/{id}/logs — channel list. */
export function useSiteLogs(siteId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteLogs(siteId),
    queryFn: () => client.get<LogChannelsResponse>(`/sites/${siteId}/logs`),
  });
}

/** GET /sites/{id}/logs/{channel}?lines= — tail of one channel. */
export function useSiteLogTail(siteId: string, channel: string | null, lines = 200) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteLogTail(siteId, channel ?? ''),
    queryFn: () =>
      client.get<LogTailResponse>(`/sites/${siteId}/logs/${channel}?lines=${lines}`),
    enabled: channel !== null && channel !== '',
  });
}

/** GET /sites/{id}/editor-usage */
export function useSiteEditorUsage(siteId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteEditorUsage(siteId),
    queryFn: () => client.get<EditorUsageResponse>(`/sites/${siteId}/editor-usage`),
  });
}


/** GET /sites/{id}/editor — the site's Site-Assistant Anthropic key status (configured + last-4 hint, never the raw key). */
export function useSiteEditorKey(siteId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteEditorKey(siteId),
    queryFn: () => client.get<EditorKeyStatus>(`/sites/${siteId}/editor`),
  });
}

/** POST /sites/{id}/editor/api-key — set/replace the site's Anthropic key in the shared gateway vault; invalidates the status so the panel reflects the new key. */
export function useSetEditorKey(siteId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      client.post<EditorKeyStatus>(`/sites/${siteId}/editor/api-key`, { key }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.siteEditorKey(siteId) }),
  });
}

/** DELETE /sites/{id}/editor/api-key — remove the site's Anthropic key (falls back to the gateway-wide key); invalidates the status. */
export function useRemoveEditorKey(siteId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.delete<EditorKeyStatus>(`/sites/${siteId}/editor/api-key`),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.siteEditorKey(siteId) }),
  });
}

/** GET /sites/{id}/chats — persisted editor-agent conversations (both sides); `session` narrows to one chat segment. */
export function useSiteChats(siteId: string, session?: string | null, limit = 100) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteChats(siteId, session),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (session) params.set('session', session);
      return client.get<ChatsResponse>(`/sites/${siteId}/chats?${params}`);
    },
  });
}

/** POST /sites/{id}/logs/{channel}/enabled — turn a log channel on/off; invalidates the channel list so the badge reflects the new state. */
export function useToggleLogChannel(siteId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, on }: { channel: string; on: boolean }) =>
      client.post<LogToggleResponse>(`/sites/${siteId}/logs/${channel}/enabled`, { on }),
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.siteLogs(siteId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.siteLogTail(siteId, vars.channel),
      });
    },
  });
}

/** GET /sites/{id}/usage */
export function useSiteUsage(siteId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.siteUsage(siteId),
    queryFn: () => client.get<SiteUsageResponse>(`/sites/${siteId}/usage`),
  });
}
